import { generateUuid } from "helpers/uuid";

const YM_COUNTER_ID = 103894847;
const YM_CLIENT_ID_ATTEMPTS = 10;
const YM_CLIENT_ID_DELAY = 200;
const YM_CLIENT_ID_TIMEOUT = YM_CLIENT_ID_ATTEMPTS * YM_CLIENT_ID_DELAY + 500;
const YANDEX_CLICK_ID_TTL = 21 * 24 * 60 * 60 * 1000;

const FIRST_URL_KEY = "first_url";
const REFERER_KEY = "referer";
const REGISTRATION_ATTEMPT_ID_KEY = "registration_attempt_id";
const REGISTRATION_UTM_KEY = "registration_utm_data";
const YANDEX_CLICK_ID_KEY = "yclid";

export type CrmUtmData = {
    clientId?: string;
    yandexClickId?: string;
    referer?: string;
    landingUrl?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    leadId?: string;
};

const registrationQueryFields = {
    utm_source: "utmSource",
    utm_medium: "utmMedium",
    utm_campaign: "utmCampaign",
    utm_term: "utmTerm",
    utm_content: "utmContent",
    lead_id: "leadId",
} as const;

type StoredYandexClickId = {
    value: string;
    expiresAt: number;
};

const hasWindow = () => typeof window !== "undefined";

const getSessionItem = (key: string) => {
    try {
        return window.sessionStorage.getItem(key);
    } catch (_err) {
        return null;
    }
};

const setSessionItem = (key: string, value: string) => {
    try {
        window.sessionStorage.setItem(key, value);
    } catch (_err) {
        return;
    }
};

const removeSessionItem = (key: string) => {
    try {
        window.sessionStorage.removeItem(key);
    } catch (_err) {
        return;
    }
};

const getLocalItem = (key: string) => {
    try {
        return window.localStorage.getItem(key);
    } catch (_err) {
        return null;
    }
};

const setLocalItem = (key: string, value: string) => {
    try {
        window.localStorage.setItem(key, value);
    } catch (_err) {
        return;
    }
};

const removeLocalItem = (key: string) => {
    try {
        window.localStorage.removeItem(key);
    } catch (_err) {
        return;
    }
};

const getYandexClickIdFromUrl = () => new URLSearchParams(window.location.search).get(YANDEX_CLICK_ID_KEY) || "";

const saveYandexClickIdFromUrl = () => {
    const yandexClickId = getYandexClickIdFromUrl();

    if (!yandexClickId) {
        return;
    }

    setLocalItem(
        YANDEX_CLICK_ID_KEY,
        JSON.stringify({
            value: yandexClickId,
            expiresAt: Date.now() + YANDEX_CLICK_ID_TTL,
        }),
    );
};

const getStoredYandexClickId = () => {
    const storedValue = getLocalItem(YANDEX_CLICK_ID_KEY);

    if (!storedValue) {
        return undefined;
    }

    try {
        const storedYandexClickId = JSON.parse(storedValue) as StoredYandexClickId;

        if (!storedYandexClickId.value || storedYandexClickId.expiresAt <= Date.now()) {
            removeLocalItem(YANDEX_CLICK_ID_KEY);
            return undefined;
        }

        return storedYandexClickId.value;
    } catch (_err) {
        return undefined;
    }
};

/** Preserve registration attribution across navigation, reloads and ATI ID authorization. */
const getRegistrationUtmData = (): CrmUtmData => {
    let stored: CrmUtmData = {};
    try {
        stored = JSON.parse(getSessionItem(REGISTRATION_UTM_KEY) || "{}");
    } catch (_err) {
        // A damaged storage entry must not prevent registration.
    }
    // Keep the first campaign as a unit; later URLs must not change or extend it.
    if (Object.values(registrationQueryFields).some((field) => stored?.[field])) {
        return stored;
    }

    const query = new URLSearchParams(window.location.search);
    const data: CrmUtmData = {};
    for (const [parameter, field] of Object.entries(registrationQueryFields)) {
        const value = query.get(parameter);
        if (value) {
            data[field] = value;
        }
    }
    setSessionItem(REGISTRATION_UTM_KEY, JSON.stringify(data));
    return data;
};

export const initCrmTracking = () => {
    if (!hasWindow()) {
        return;
    }

    if (!getSessionItem(FIRST_URL_KEY)) {
        setSessionItem(FIRST_URL_KEY, window.location.href);
    }

    if (getSessionItem(REFERER_KEY) === null) {
        setSessionItem(REFERER_KEY, document.referrer || "");
    }

    saveYandexClickIdFromUrl();
    getRegistrationUtmData();
};

export const getRegistrationAttemptId = () => {
    if (!hasWindow()) {
        return "";
    }

    const storedRegistrationAttemptId = getSessionItem(REGISTRATION_ATTEMPT_ID_KEY);

    if (storedRegistrationAttemptId) {
        return storedRegistrationAttemptId;
    }

    const registrationAttemptId = generateUuid();
    setSessionItem(REGISTRATION_ATTEMPT_ID_KEY, registrationAttemptId);

    return registrationAttemptId;
};

export const getStoredRegistrationAttemptId = () => {
    if (!hasWindow()) {
        return "";
    }

    return getSessionItem(REGISTRATION_ATTEMPT_ID_KEY) || "";
};

export const clearRegistrationAttemptId = () => {
    removeSessionItem(REGISTRATION_ATTEMPT_ID_KEY);
    removeSessionItem(REGISTRATION_UTM_KEY);
};

const getYmClientId = () =>
    new Promise<string>((resolve) => {
        if (!hasWindow()) {
            resolve("");
            return;
        }

        let isResolved = false;

        const resolveOnce = (value: string) => {
            if (isResolved) {
                return;
            }

            isResolved = true;
            window.clearTimeout(timeoutId);
            resolve(value);
        };

        const timeoutId = window.setTimeout(() => resolveOnce(""), YM_CLIENT_ID_TIMEOUT);

        const capture = (attempts: number) => {
            if (typeof window.ym === "function") {
                try {
                    window.ym(YM_COUNTER_ID, "getClientID", (clientId) => resolveOnce(clientId || ""));
                } catch (_err) {
                    resolveOnce("");
                }
                return;
            }

            if (attempts <= 0) {
                resolveOnce("");
                return;
            }

            window.setTimeout(() => capture(attempts - 1), YM_CLIENT_ID_DELAY);
        };

        capture(YM_CLIENT_ID_ATTEMPTS);
    });

export const getCrmUtmData = async (): Promise<CrmUtmData> => {
    if (!hasWindow()) {
        return {};
    }

    initCrmTracking();

    const clientId = await getYmClientId();
    const yandexClickId = getStoredYandexClickId();
    const referer = getSessionItem(REFERER_KEY) || undefined;
    const landingUrl = getSessionItem(FIRST_URL_KEY) || window.location.href;

    return {
        ...getRegistrationUtmData(),
        ...(clientId ? { clientId } : {}),
        ...(yandexClickId ? { yandexClickId } : {}),
        ...(referer ? { referer } : {}),
        ...(landingUrl ? { landingUrl } : {}),
    };
};
